import { TablePagination, TablePaginationProps, useTheme } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { Action, ActionPosition, AtonUnitDetails } from "~/api/GeneratedApi";
import GenericTable, {
  CustomColumn,
  CustomTableProps,
} from "~/components/GenericTable";
import PaddedBox from "~/components/PaddedBox";
import { useDateFormatter } from "~/utils/hooks/useDateFormatter";

interface Props {
  atonData: AtonUnitDetails;
  small?: boolean;
}

type PositionAction = ActionPosition & Action;

const AtoNActionsTab = ({ atonData, small }: Props): JSX.Element => {
  const { t } = useTranslation("aton-position-action");
  const dateFormatter = useDateFormatter();
  const theme = useTheme();

  const { actions } = atonData;

  const positionActions = React.useMemo(() => {
    const anch: PositionAction[] = [];
    actions.forEach((a) => {
      if (a.position) anch.push({ ...a.position, ...a });
    });
    return anch;
  }, [actions]);

  const columns: CustomColumn<PositionAction>[] = React.useMemo(
    () => [
      {
        title: t("date"),
        field: "timestamp",
        render: ({ timestamp }) =>
          timestamp ? dateFormatter({ date: timestamp }) : "",
      },
      {
        title: t("perr"),
        field: "perr",
      },
      {
        title: t("vatuDifference"),
        field: "vatuDifference",
      },
      {
        title: t("vatuDifferenceToInstallation"),
        field: "vatuDifferenceToInstallation",
      },
      {
        title: t("depth"),
        field: "depth",
      },
    ],
    [dateFormatter, t],
  );

  const tableProps: CustomTableProps<PositionAction> = React.useMemo(
    () => ({
      title: t("aton:list-position-actions"),
      ...(small
        ? {
            options: {
              pageSizeOptions: [5, 10, 15, 25, 50],
              pageSize: 10,
            },
            components: {
              Container: (props) => (
                <div
                  {...props}
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    border: "none",
                  }}
                />
              ),
              Pagination: (props: TablePaginationProps) => {
                const { style } = props;
                return (
                  <TablePagination
                    {...props}
                    style={{
                      borderBottomRightRadius: theme.shape.borderRadius,
                      ...style,
                    }}
                  />
                );
              },
            },
          }
        : {}),
    }),
    [small, t, theme.shape.borderRadius],
  );

  const table = React.useMemo(
    () => (
      <GenericTable
        data={positionActions ?? []}
        columns={columns}
        tableProps={tableProps}
      />
    ),
    [columns, positionActions, tableProps],
  );

  return small ? table : <PaddedBox>{table}</PaddedBox>;
};

export default AtoNActionsTab;
